import { useState, useEffect } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { CalendarIcon, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface ChlorineConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wellId: number;
  editingConfig: any | null;
  onSuccess: () => void;
}

export function ChlorineConfigModal({ open, onOpenChange, wellId, editingConfig, onSuccess }: ChlorineConfigModalProps) {
  const [activeFrom, setActiveFrom] = useState<Date | undefined>();
  const [activeTo, setActiveTo] = useState<Date | undefined>();
  const [gallonsPerInch, setGallonsPerInch] = useState("");
  const [lbsCl2PerGallon, setLbsCl2PerGallon] = useState("");
  const [configNotes, setConfigNotes] = useState("");
  
  // Tank sizing fields for auto-calculation
  const [tankCapacityGallons, setTankCapacityGallons] = useState("");
  const [tankUsableHeightInches, setTankUsableHeightInches] = useState("");
  const [chlorineStrengthLbsPerGal, setChlorineStrengthLbsPerGal] = useState("");
  const [useAutoCalc, setUseAutoCalc] = useState(false);

  // Display-only numeric formatter (DECIMAL values can be strings; avoid calling .toFixed on unknown types).
  const fmtNum = (v: any, d: number) => {
    if (typeof v === "number" && Number.isFinite(v)) return v.toFixed(d);
    if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v).toFixed(d);
    return "—";
  };

  const parseFinite = (v: any) => {
    const n = typeof v === "number" ? v : (typeof v === "string" ? Number(v) : NaN);
    return Number.isFinite(n) ? n : null;
  };

  const createMutation = trpc.chlorineConfig.create.useMutation({
    onSuccess: () => {
      toast.success("Chlorine config created successfully");
      resetForm();
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create config");
    },
  });

  const updateMutation = trpc.chlorineConfig.update.useMutation({
    onSuccess: () => {
      toast.success("Chlorine config updated successfully");
      resetForm();
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update config");
    },
  });

  const resetForm = () => {
    setActiveFrom(undefined);
    setActiveTo(undefined);
    setGallonsPerInch("");
    setLbsCl2PerGallon("");
    setConfigNotes("");
    setTankCapacityGallons("");
    setTankUsableHeightInches("");
    setChlorineStrengthLbsPerGal("");
    setUseAutoCalc(false);
  };
  
  // Auto-calculate gallonsPerInch when tank sizing fields are provided
  const calculatedGallonsPerInch = tankCapacityGallons && tankUsableHeightInches
    ? (() => {
        const capacity = parseFloat(tankCapacityGallons);
        const height = parseFloat(tankUsableHeightInches);
        if (!isFinite(capacity) || !isFinite(height) || capacity <= 0 || height <= 0) return null;
        return (capacity / height).toFixed(4);
      })()
    : null;

  // Preview helper (UI-only): assume Inches Used = 1.00
  const effectiveGallonsPerInch = parseFinite(gallonsPerInch) ?? (calculatedGallonsPerInch ? parseFinite(calculatedGallonsPerInch) : null);
  const effectiveLbsPerGal = parseFinite(lbsCl2PerGallon) ?? parseFinite(chlorineStrengthLbsPerGal);
  const previewGallonsDelivered = effectiveGallonsPerInch != null ? effectiveGallonsPerInch * 1.0 : null;
  const previewLbsCl2 = (previewGallonsDelivered != null && effectiveLbsPerGal != null) ? previewGallonsDelivered * effectiveLbsPerGal : null;

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  // Populate form when editing
  useEffect(() => {
    if (editingConfig) {
      setActiveFrom(editingConfig.activeFrom ? new Date(editingConfig.activeFrom) : undefined);
      setActiveTo(editingConfig.activeTo ? new Date(editingConfig.activeTo) : undefined);
      setGallonsPerInch(editingConfig.gallonsPerInch?.toString() || "");
      setLbsCl2PerGallon(editingConfig.lbsCl2PerGallon?.toString() || "");
      setTankCapacityGallons(editingConfig.tankCapacityGallons?.toString() || "");
      setTankUsableHeightInches(editingConfig.tankUsableHeightInches?.toString() || "");
      setChlorineStrengthLbsPerGal(editingConfig.chlorineStrengthLbsPerGal?.toString() || "");
      setUseAutoCalc(false);
      setConfigNotes(editingConfig.configNotes || "");
    } else {
      setActiveFrom(undefined);
      setActiveTo(undefined);
      setGallonsPerInch("");
      setLbsCl2PerGallon("");
      setTankCapacityGallons("");
      setTankUsableHeightInches("");
      setChlorineStrengthLbsPerGal("");
      setUseAutoCalc(false);
      setConfigNotes("");
    }
  }, [editingConfig]);


  const isEditMode = !!editingConfig;
  const isLoading = createMutation.isPending || updateMutation.isPending;

  const LockedInfoIcon = ({ label, text }: { label: string; text: string }) => (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full w-4 h-4 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label={`Why ${label} is locked`}
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-sm">
          <p className="text-sm">{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const handleSubmit = () => {
    if (!activeFrom) {
      toast.error("Active From date is required");
      return;
    }

    const hasManualGallonsPerInch = gallonsPerInch && parseFloat(gallonsPerInch) > 0;
    const hasTankSizing = tankCapacityGallons && tankUsableHeightInches;

    // Allow either manual gallonsPerInch OR (capacity + height) with server-side auto-calc.
    if (!hasManualGallonsPerInch && !hasTankSizing) {
      toast.error("Provide gallons per inch, or provide tank capacity + usable height");
      return;
    }

    if (activeTo && activeFrom >= activeTo) {
      toast.error("Active To must be after Active From");
      return;
    }

    if (isEditMode) {
      updateMutation.mutate({
        id: editingConfig.id,
        activeTo: activeTo || undefined,
        configNotes: configNotes || undefined,
      });
    } else {
      createMutation.mutate({
        wellId,
        // Prefer manual gallonsPerInch unless user explicitly chooses auto-calc.
        gallonsPerInch: useAutoCalc ? undefined : (gallonsPerInch || undefined),
        lbsCl2PerGallon: lbsCl2PerGallon || undefined,
        tankCapacityGallons: tankCapacityGallons || undefined,
        tankUsableHeightInches: tankUsableHeightInches || undefined,
        chlorineStrengthLbsPerGal: chlorineStrengthLbsPerGal || undefined,
        activeFrom,
        activeTo: activeTo || undefined,
        configNotes: configNotes || undefined,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit Config" : "Add New Config"}</DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? "Update activeTo or notes for this historical config."
              : "Create a new chlorine system configuration. New active configs will auto-close the previous one. Tank sizing is stored per well and time period. If you replace a tank, add a new config with a new effective date."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="activeFrom">Active From *</Label>
              {isEditMode && (
                <LockedInfoIcon
                  label="Active From"
                  text="Configuration dates determine which calculation rules apply to each log. Past dates are locked to preserve historical accuracy."
                />
              )}
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !activeFrom && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {activeFrom ? format(activeFrom, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={activeFrom}
                  onSelect={setActiveFrom}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {isEditMode && (
              <p className="text-xs text-muted-foreground">Cannot change start date</p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="activeTo">Active To (optional)</Label>
              {isEditMode && (
                <LockedInfoIcon
                  label="Active To"
                  text="Configuration dates determine which calculation rules apply to each log. Past dates are locked to preserve historical accuracy."
                />
              )}
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !activeTo && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {activeTo ? format(activeTo, "PPP") : <span>Pick a date or leave blank for active</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={activeTo}
                  onSelect={setActiveTo}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {!isEditMode && (
              <p className="text-xs text-muted-foreground text-orange-600">
                Leave blank to make this the current active config (closes previous)
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="gallonsPerInch">Gallons per Inch *</Label>
              {isEditMode && (
                <LockedInfoIcon
                  label="Gallons per Inch"
                  text="This value is locked because it has already been used by daily logs. Locking prevents historical reports from changing during audits."
                />
              )}
            </div>
            <Input
              id="gallonsPerInch"
              type="number"
              step="0.01"
              value={gallonsPerInch}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGallonsPerInch(e.target.value)}
              placeholder="e.g. 10.5"
              disabled={isEditMode}
            />
            {isEditMode && (
              <p className="text-xs text-muted-foreground">Cannot change tank capacity</p>
            )}
          </div>

          {!isEditMode && (
            <div className="space-y-2">
              <Label>Tank Sizing (optional)</Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="tankCapacityGallons" className="text-xs">Tank Capacity (gal)</Label>
                  <Input
                    id="tankCapacityGallons"
                    type="number"
                    step="0.01"
                    value={tankCapacityGallons}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTankCapacityGallons(e.target.value)}
                    placeholder="e.g. 2500"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="tankUsableHeightInches" className="text-xs">Usable Height (in)</Label>
                  <Input
                    id="tankUsableHeightInches"
                    type="number"
                    step="0.01"
                    value={tankUsableHeightInches}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTankUsableHeightInches(e.target.value)}
                    placeholder="e.g. 72"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                  Calculated gallons/in: {calculatedGallonsPerInch ?? "—"}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!calculatedGallonsPerInch}
                  onClick={() => {
                    if (!calculatedGallonsPerInch) return;
                    // Copy calculated value into gallonsPerInch (manual override still allowed after).
                    setGallonsPerInch(calculatedGallonsPerInch);
                    setUseAutoCalc(true);
                  }}
                >
                  Use calculated value
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="useAutoCalc"
                  type="checkbox"
                  checked={useAutoCalc}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUseAutoCalc(e.target.checked)}
                />
                <Label htmlFor="useAutoCalc" className="text-xs">
                  Auto-calc gallons/in from capacity + height (server will compute)
                </Label>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="lbsCl2PerGallon">Lbs Cl₂ per Gallon (optional)</Label>
              {isEditMode && (
                <LockedInfoIcon
                  label="Lbs Cl₂ per Gallon"
                  text="This value cannot be edited once logs exist. If chemical strength changes, create a new configuration with a new effective date."
                />
              )}
            </div>
            {!isEditMode && (
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="lbsCl2Preset" className="text-xs text-muted-foreground">Preset</Label>
                <select
                  id="lbsCl2Preset"
                  className="h-9 rounded-md border bg-background px-3 text-sm"
                  defaultValue="custom"
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    const v = e.target.value;
                    if (v === "custom") return;
                    // Overwrite inputs intentionally for speed/clarity.
                    setLbsCl2PerGallon(v);
                    setChlorineStrengthLbsPerGal(v);
                  }}
                >
                  <option value="custom">Custom</option>
                  <option value="0.0125">12.5% (0.0125)</option>
                  <option value="0.0100">10% (0.0100)</option>
                  <option value="0.00825">8.25% (0.00825)</option>
                </select>
              </div>
            )}
            <Input
              id="lbsCl2PerGallon"
              type="number"
              step="0.0001"
              value={lbsCl2PerGallon}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLbsCl2PerGallon(e.target.value)}
              placeholder="e.g. 0.0125"
              title="How strong is your bleach solution? Example: 12.5% sodium hypochlorite ≈ 0.0125 lbs Cl₂ per gallon."
              disabled={isEditMode}
            />
            {!isEditMode && (
              <p className="text-xs text-muted-foreground">
                Used to calculate lbs of chlorine: inches used × gallons/in × lbs/gal.
              </p>
            )}
            {!isEditMode && (
              <p className="text-xs text-muted-foreground">
                {previewGallonsDelivered != null && previewLbsCl2 != null
                  ? `Preview (1.00 inch): ~${fmtNum(previewGallonsDelivered, 2)} gal delivered, ~${fmtNum(previewLbsCl2, 2)} lbs Cl₂`
                  : "Preview (1.00 inch): — (enter gallons/in and lbs/gal)"}
              </p>
            )}
            {isEditMode && (
              <p className="text-xs text-muted-foreground">Cannot change solution concentration</p>
            )}
          </div>

          {!isEditMode && (
            <div className="space-y-2">
              <Label htmlFor="chlorineStrengthLbsPerGal">Chlorine Strength (lbs/gal) (optional)</Label>
              <Input
                id="chlorineStrengthLbsPerGal"
                type="number"
                step="0.0001"
                value={chlorineStrengthLbsPerGal}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setChlorineStrengthLbsPerGal(e.target.value)}
                placeholder="e.g. 0.0125"
              />
              <p className="text-xs text-muted-foreground">
                Optional helper field. Tank sizing is stored per well and time period.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="configNotes">Config Notes (optional)</Label>
            <Textarea
              id="configNotes"
              value={configNotes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setConfigNotes(e.target.value)}
              placeholder="Why this config was created (tank swap, recalibration, etc.)"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isLoading}
            className="flex-1"
          >
            {isLoading ? "Saving..." : (isEditMode ? "Update" : "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
